import { useEffect } from 'react';
import { directusAPI } from '../../../api';
import { hasGeneratedDraftContent, resolveTargetStep } from '../lib/editorBlocks';
import { normalizeSourcesList } from '../lib/sources';
import { log } from '../services/articleGenerationApi';

/**
 * Loads wizard state from Directus: an existing article from the `?id=` url param,
 * or (only for a pristine wizard) the most recent draft of the last 24 hours.
 * Effects run in the same order and with the same dependencies as in the page before.
 */
export function useWizardArticleLoader(ctx) {
  const {
    navigate,
    searchParams,
    getValidStepNumber,
    navigateToStep,
    isCreatingRef,
    isCreatingArticle,
    setIsCreatingArticle,
    isAutosaving,
    contentType,
    articleContext,
    generatedArticleId,
    setGeneratedArticleId,
    setArticleData,
    setHasInitialArticle,
    setContentType,
    setArticleContext,
    setKeywords,
    setSources,
    setToneOfVoice,
    setComplexity,
    setLanguage,
    setSelectedProject,
    setIncludeImages,
    setThumbnailSource,
    setSelectedStructure,
    setLastSaved,
    setIsDraftGenerated,
    setEditorHasContent,
    setShowEditor,
    setSaveError,
  } = ctx;

  // Check for recent drafts on component mount
  useEffect(() => {
    const checkForRecentDraft = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');

        if (!userId || !token) return;

        // Look for recent drafts in the last 24 hours
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        const response = await directusAPI.get('/items/articles', {
          params: {
            filter: {
              status: 'draft',
              date_created: { _gte: oneDayAgo }
            },
            sort: '-date_created',
            limit: 1
          }
        });

        if (response.data.data.length > 0) {
          const recentDraft = response.data.data[0];
          log('Found recent draft:', recentDraft.id);

          // Only auto-load if we don't have any current data AND no ID is being processed AND not creating new article
          const articleId = searchParams.get('id');
          if (!contentType && !articleContext && !generatedArticleId && !articleId && !isCreatingRef.current && !isCreatingArticle) {
            log('Auto-loading recent draft');
            setGeneratedArticleId(recentDraft.id);
            setArticleData(recentDraft);
            setContentType(recentDraft.content_type || '');
            setArticleContext(recentDraft.article_context || '');
            setKeywords(recentDraft.keywords || []);
            setSources(normalizeSourcesList(recentDraft.sources));
            setToneOfVoice(recentDraft.tone_of_voice || 'casual');
            setComplexity(recentDraft.fog_index || 'fog index: 15');
            setLanguage(recentDraft.language || 'polski');
            setSelectedProject(recentDraft.project || '');
            setIncludeImages(recentDraft.article_thumbnail_included !== false);
            setThumbnailSource(recentDraft.article_thumbnail_source || 'pexels');
            setSelectedStructure(
              recentDraft.article_linked_structure?.id
              || recentDraft.article_linked_structure
              || null
            );
            setLastSaved(recentDraft.date_updated || recentDraft.date_created);

            // Redirect to this draft
            const typeParam = recentDraft.content_type ? `&type=${recentDraft.content_type}` : '';
            navigate(`/writer/articles/create-article/step/setup?id=${recentDraft.id}${typeParam}`, { replace: true });
          }
        }
      } catch (error) {
        log('Error checking for recent drafts:', error);
      }
    };

    checkForRecentDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- check recent drafts when URL params change only
  }, [searchParams]);

  // Load existing article if ID is provided in URL params
  useEffect(() => {
    const articleId = searchParams.get('id');
    if (articleId && articleId !== generatedArticleId?.toString()) {
      log('🔍 LOAD_ARTICLE DEBUG: Loading article ID:', articleId);
      log('🔍 LOAD_ARTICLE DEBUG: Current generatedArticleId:', generatedArticleId);
      log('🔍 LOAD_ARTICLE DEBUG: isAutosaving:', isAutosaving);
      
      // Don't reload article during autosave to prevent EditorJS reset
      if (isAutosaving) {
        log('🔍 LOAD_ARTICLE DEBUG: Skipping article reload during autosave');
        return;
      }
      
      // Reset creation flags since we're loading existing article
      isCreatingRef.current = false;
      setIsCreatingArticle(false);

      const loadExistingArticle = async () => {
        try {
          log('Loading existing article:', articleId);
          const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');

          const response = await directusAPI.get(`/items/articles/${articleId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const article = response.data.data;
          log('Loaded existing article:', article);

          // Set all the form data from the existing article
          setGeneratedArticleId(article.id);
          setArticleData(article);
          setHasInitialArticle(true);
          setContentType(article.content_type || '');
          setArticleContext(article.article_context || '');
          setKeywords(article.keywords || []);
          setSources(normalizeSourcesList(article.sources));
          setToneOfVoice(article.tone_of_voice || 'casual');
          setComplexity(article.fog_index || 'fog index: 15');
          setLanguage(article.language || 'polski');
          setSelectedProject(article.project || '');
          setIncludeImages(article.article_thumbnail_included !== false);
          setThumbnailSource(article.article_thumbnail_source || 'pexels');
          setSelectedStructure(
            article.article_linked_structure?.id
            || article.article_linked_structure
            || null
          );
          setLastSaved(article.date_updated || article.date_created);

          // Check if draft has been generated by looking at article_draft content
          const hasGeneratedDraft = hasGeneratedDraftContent(article);

          // A failed final generation keeps its draft and can be retried.
          const finalDraftGenerated = hasGeneratedDraft;
          setIsDraftGenerated(finalDraftGenerated);
          setEditorHasContent(hasGeneratedDraft); // Set editor content state
          setShowEditor(hasGeneratedDraft); // Show editor if there's content

          log('🔄 LOAD ARTICLE: Setting states:', {
            editorHasContent: hasGeneratedDraft,
            showEditor: hasGeneratedDraft,
            articleStatus: article.status,
            createDraftDisabled: hasGeneratedDraft,
            generateButtonWillShow: hasGeneratedDraft
          });

          // Determine the appropriate step based on article data
          const targetStep = resolveTargetStep(article, hasGeneratedDraft);

          // Navigate to the appropriate step if we're not already there
          const currentStepIndex = getValidStepNumber();
          if (currentStepIndex !== targetStep) {
            navigateToStep(targetStep);
          }

        } catch (error) {
          log('Error loading existing article:', error);
          setSaveError('Error loading article: ' + error.message);
        }
      };

      loadExistingArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when URL id changes; omit generatedArticleId to avoid reload loops
  }, [searchParams, isAutosaving]);
}
